import { PathParams } from "@constellation-academy/epic-ui";
import { useCallback, useMemo } from "react";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import type { personalizedNotificationTemplateEdit_EditPersonalizedNotificationTemplateMutation } from "@relay/personalizedNotificationTemplateEdit_EditPersonalizedNotificationTemplateMutation.graphql";
import type { personalizedNotificationTemplateEdit_PersonalizedNotificationTemplateFragment$key } from "@relay/personalizedNotificationTemplateEdit_PersonalizedNotificationTemplateFragment.graphql";
import type { personalizedNotificationTemplateEdit_Query } from "@relay/personalizedNotificationTemplateEdit_Query.graphql";
import { PersonalizedNotificationTemplateForm } from "./parts/personalized-notification-template-form";
import type { PersonalizedNotificationTemplateFormValues } from "./parts/personalized-notification-template-form/personalized-notification-template-form.types";
import {
	QUERY,
	PERSONALIZED_NOTIFICATION_TEMPLATE_FRAGMENT,
	EDIT_PERSONALIZED_NOTIFICATION_TEMPLATE_MUTATION,
} from "./personalized-notification-template-edit.graphql";
import { BaseScreen } from "../../components/base-screen";
import { PersonalizedNotificationTemplatesPath } from "../personalized-notification-templates";

export const PersonalizedNotificationTemplateEditScreen = () => {
	const { t } = useTranslation("common");
	const { personalizedNotificationTemplateId } =
		useParams<PathParams<typeof PersonalizedNotificationTemplatesPath>>();

	const query = useLazyLoadQuery<personalizedNotificationTemplateEdit_Query>(
		QUERY,
		{
			id: personalizedNotificationTemplateId!,
		},
		{ fetchPolicy: "network-only" },
	);

	const [editPersonalizedNotificationTemplate, isEditingPersonalizedNotificationTemplate] =
		useMutation<personalizedNotificationTemplateEdit_EditPersonalizedNotificationTemplateMutation>(
			EDIT_PERSONALIZED_NOTIFICATION_TEMPLATE_MUTATION,
		);

	const personalizedNotificationTemplate =
		useFragment<personalizedNotificationTemplateEdit_PersonalizedNotificationTemplateFragment$key>(
			PERSONALIZED_NOTIFICATION_TEMPLATE_FRAGMENT,
			query.node ?? null,
		);

	const formInitialValues: PersonalizedNotificationTemplateFormValues = useMemo(
		() => ({
			title: personalizedNotificationTemplate?.template.title ?? "",
			message: personalizedNotificationTemplate?.template.message ?? "",
		}),
		[personalizedNotificationTemplate],
	);

	const handlePersonalizedNotificationTemplateFormOnSubmit = useCallback(
		(values: PersonalizedNotificationTemplateFormValues) => {
			editPersonalizedNotificationTemplate({
				variables: {
					input: {
						id: personalizedNotificationTemplateId!,
						message: values.message,
						title: values.title,
					},
				},
				onCompleted: () => {
					toast.success(t("personalizedNotificationTemplates.editSuccess"));
				},
				onError: () => {
					toast.error(t("personalizedNotificationTemplates.editError"));
				},
			});
		},
		[editPersonalizedNotificationTemplate, personalizedNotificationTemplateId],
	);

	return (
		<BaseScreen
			title={t("personalizedNotificationTemplates.editTitle", {
				title: personalizedNotificationTemplate?.template.title,
			})}
		>
			<PersonalizedNotificationTemplateForm
				initialValues={formInitialValues}
				variables={personalizedNotificationTemplate?.template.variables ?? []}
				isSubmitting={isEditingPersonalizedNotificationTemplate}
				onSubmit={handlePersonalizedNotificationTemplateFormOnSubmit}
			/>
		</BaseScreen>
	);
};
